import { Fragment, useState } from 'react';
import { FragmentData, gql } from '@ts-gql/tag';
import { useApolloClient, useMutation } from '@ts-gql/apollo';

import { Button } from '@reckon-web/button';
import { Stack } from '@reckon-web/stack';
import { useToasts } from '@reckon-web/toast';
import { SideDrawer } from '@reckon-web/side-drawer';
import {
  AutocompleteField,
  entityOptionRenderer,
} from '@reckon-web/autocomplete';
import { field, useForm, useFormSnapshot, useSubmit } from '@reckon-web/forms';

import { useError } from '../../../../utilities/hooks/useError';
import { DiscardConfirmationDialog } from '../../../common/dialogs/DiscardConfirmationDialog';
import { CLIENT_SEARCH } from '../../../SearchClients';
import { ClientDetailsFragment } from '../..';
type Client = {
  toggleIsOpen: () => void;
  client: FragmentData<typeof ClientDetailsFragment>;
};
const debtorGroupForm = field.object({
  debtorGroup: field.autocomplete()(),
});
// component
export const EditDebtorGroupDrawer = ({ toggleIsOpen, client }: Client) => {
  const apolloClient = useApolloClient();
  const { addToast } = useToasts();
  const form = useForm(debtorGroupForm, {
    debtorGroup: {
      value: client?.debtor?.head?.id || '',
      label: client?.debtor?.head?.sortName || '',
    },
  });
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const { isDirty } = useFormSnapshot(form);
  const [UpdatedebtorGroup, { loading }] = useMutation(UPDATE_DEBTOR_GROUP);
  const handleError = useError();
  const handleSaveAndCloseSubmit = useSubmit(form, (value) => {
    UpdatedebtorGroup({
      variables: { id: client.id, debtorId: value.debtorGroup?.value || '' },
    })
      .then(() => {
        toggleIsOpen();
        addToast({
          title: 'Debtor group update successful',
          message: `Successfully updated the debtor group to ${value.debtorGroup?.label}`,
          tone: 'positive',
        });
      })
      .catch((error) => {
        handleError({ title: 'Failed to update debtor group', error });
      });
  });
  const handleCancel = () => {
    isDirty ? setIsDiscardDialogOpen(true) : toggleIsOpen();
  };
  return (
    <Fragment>
      <SideDrawer>
        <SideDrawer.Form onSubmit={handleSaveAndCloseSubmit}>
          <SideDrawer.Header>
            <SideDrawer.Title>Change debtor group</SideDrawer.Title>
          </SideDrawer.Header>
          <SideDrawer.Body>
            <Stack marginY="xlarge" gap="xlarge" paddingX="xlarge">
              <AutocompleteField
                label="Search clients to assign a new debtor group"
                {...form.fields.debtorGroup.props}
                loadOptions={async (inputValue) => {
                  const { data } = await apolloClient.query({
                    query: CLIENT_SEARCH,
                    variables: { searchInput: inputValue },
                  });
                  return (
                    data?.searchClients?.map((client) => {
                      return {
                        value: client?.id || '',
                        label: client?.sortName || '',
                      };
                    }) || []
                  );
                }}
                renderOption={entityOptionRenderer}
              />
            </Stack>
          </SideDrawer.Body>
          <SideDrawer.Footer>
            <Button label="Save & close" type="submit" loading={loading} />
            <Button
              label="Cancel"
              weight="none"
              onClick={handleCancel}
              disabled={loading}
            />
          </SideDrawer.Footer>
        </SideDrawer.Form>
      </SideDrawer>
      <DiscardConfirmationDialog
        isDiscardDialogOpen={isDiscardDialogOpen}
        setIsDiscardDialogOpen={setIsDiscardDialogOpen}
        callback={() => toggleIsOpen()}
      />
    </Fragment>
  );
};
export const UPDATE_DEBTOR_GROUP = gql`
  mutation UpdateDebtorGroup(
    $id: ID!
    $debtorId: ID!
    $showSecGroup: Boolean = false
  ) {
    updateClient(id: $id, input: { debtor: { id: $debtorId } }) {
      id
      ...ClientDetailsPage_client
    }
  }
` as import('../../../../../__generated__/ts-gql/UpdateDebtorGroup').type;
